/**
 * useCalendarRefresh — on-demand calendar refresh for views that show calendar events.
 *
 * Mount this hook in Today/Upcoming views (not globally). It will:
 * 1. Load the cached calendar data once per session (instant render; later mounts are free)
 * 2. Fetch fresh data if the last fetch is stale (>10 min)
 * 3. Re-fetch on foreground if stale, and re-expand when the day changed
 * 4. Follow calendar config changes (added, edited, deleted - also from another device)
 *
 * No polling — purely event-driven (Fas 3b).
 */

import { useEffect, useMemo, useRef } from "react"
import { appLifecycleHub } from "@/shared/utils/platform/appLifecycleHub"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { useCalendarStore } from "./calendarStore"
import type { ICalCalendar } from "./types"
import { normalizeCalendarConfigs } from "./types"

const STALE_THRESHOLD_MS = 10 * 60 * 1000 // 10 minutes
const NO_CALENDARS: ICalCalendar[] = []

/** Shared timestamp so multiple mounted views don't double-fetch */
let lastGlobalFetchAt = 0

export function useCalendarRefresh() {
  const fetchAndUpdateCalendars = useCalendarStore(
    (s) => s.fetchAndUpdateCalendars,
  )
  const loadCachedCalendars = useCalendarStore((s) => s.loadCachedCalendars)
  const ensureCurrentWindow = useCalendarStore((s) => s.ensureCurrentWindow)
  const applyConfigs = useCalendarStore((s) => s.applyConfigs)

  const { value: rawConfigs, isLoading: configsLoading } = useSetting<ICalCalendar[]>(
    "#ical-calendars",
    NO_CALENDARS,
  )
  // Signature of what matters for fetching and display; unchanged configs do nothing
  const configs = useMemo(() => normalizeCalendarConfigs(rawConfigs), [rawConfigs])
  const configKey = useMemo(
    () => configs.map((c) => `${c.id}|${c.url}|${c.color}|${c.name}`).join("\n"),
    [configs],
  )
  const appliedKey = useRef<string | null>(null)

  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    const fetchIfStale = () => {
      const elapsed = Date.now() - lastGlobalFetchAt
      if (elapsed > STALE_THRESHOLD_MS) {
        lastGlobalFetchAt = Date.now()
        void fetchAndUpdateCalendars()
      }
    }

    // Load cache → fetch if stale
    void loadCachedCalendars().then(() => {
      if (mountedRef.current) fetchIfStale()
    })

    // Foreground handler — new day, and re-fetch if stale
    const removeForeground = appLifecycleHub.on("foreground", () => {
      void ensureCurrentWindow()
      fetchIfStale()
    })

    return () => {
      mountedRef.current = false
      removeForeground()
    }
  }, [fetchAndUpdateCalendars, loadCachedCalendars, ensureCurrentWindow])

  useEffect(() => {
    if (configsLoading) return
    if (appliedKey.current === null) {
      // First value seen by this mount: the cache load and the stale fetch cover it
      appliedKey.current = configKey
      return
    }
    if (appliedKey.current === configKey) return
    appliedKey.current = configKey
    void applyConfigs(configs)
  }, [configsLoading, configKey, configs, applyConfigs])
}
